import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { ChevronDown, ChevronRight } from 'lucide-react';

const CollapsibleCode = ({ language, value }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="my-6 rounded-lg overflow-hidden shadow-lg border border-gray-200">
      {/* Collapsible header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-between cursor-pointer group"
      >
        <div className="flex items-center space-x-2">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-600" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-600" />
          )}
          <span className="text-sm text-gray-600 font-medium">
            {isExpanded ? 'Hide code' : 'Show code'}
          </span>
        </div>
      </button>

      {/* Code content */}
      {isExpanded && (
        <div className="overflow-x-auto always-show-scrollbar">
          <SyntaxHighlighter
            language={language}
            style={oneDark}
            customStyle={{
              margin: 0,
              padding: '1.5rem',
              fontSize: '0.9rem',
              backgroundColor: '#282c34',
            }}
            showLineNumbers={true}
            wrapLongLines={false}
          >
            {value}
          </SyntaxHighlighter>
        </div>
      )}
    </div>
  );
};

const MarkdownRenderer = ({ basePath, showCode = true }) => {
  const [markdownContent, setMarkdownContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const basename = basePath.substring(basePath.lastIndexOf('/') + 1);

  useEffect(() => {
    const fetchMarkdown = async () => {
      try {
        const markdownFilePath = `${basePath}/${basename}.md`;
        const response = await fetch(markdownFilePath);
        if (!response.ok) {
          throw new Error(`Error fetching markdown file: ${response.status}`);
        }
        const text = await response.text();
        if (text.includes('<!DOCTYPE html>')) {
          throw new Error('Error: Received HTML content instead of markdown content.');
        }
        setMarkdownContent(text);
      } catch (error) {
        console.error(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMarkdown();
  }, [basePath, basename]);

  // Custom components for markdown rendering
  const components = {
    // Code block component with syntax highlighting and scrolling
    code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : '';
      const value = String(children).replace(/\n$/, '');

      // If showCode is false, skip any code blocks declared as ```python
      if (!showCode && language === 'python') {
        return null;
      }

      if (inline) {
        return (
          <code 
            className="bg-gray-100 text-gray-800 rounded px-1 py-0.5 text-sm font-mono"
            {...props}
          >
            {children}
          </code>
        );
      }

      return <CollapsibleCode language={language} value={value} />;
    },

    // Custom heading components
    h1: ({ children }) => (
      <h1 className="text-4xl font-bold mb-6 mt-8 text-gray-900">{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-3xl font-semibold mb-4 mt-6 text-gray-800">{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-2xl font-medium mb-3 mt-5 text-gray-700">{children}</h3>
    ),
    h4: ({ children }) => (
      <h4 className="text-xl font-medium mb-2 mt-4 text-gray-700">{children}</h4>
    ),

    // Paragraph component
    p: ({ children }) => (
      <p className="text-gray-600 leading-relaxed mb-4">{children}</p>
    ),

    // List components
    ul: ({ children }) => (
      <ul className="list-disc pl-6 mb-4 text-gray-600">{children}</ul>
    ),
    ol: ({ children }) => (
      <ol className="list-decimal pl-6 mb-4 text-gray-600">{children}</ol>
    ),
    li: ({ children }) => <li className="mb-2">{children}</li>,

    // Blockquote component
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-blue-500 pl-4 py-2 mb-4 bg-blue-50 rounded">
        {children}
      </blockquote>
    ),

    // Image component with caption support
    img: ({ src, alt }) => {
      const adjustedSrc = src ? encodeURI(`${basePath}/${decodeURIComponent(src)}`) : '';
      const shouldDisplayCaption = alt && !['png', 'jpg', 'jpeg'].includes(alt.toLowerCase());

      return (
        <figure className="my-6">
          <img
            src={adjustedSrc}
            alt={alt}
            className="rounded-lg shadow-lg max-w-full mx-auto"
          />
          {shouldDisplayCaption && (
            <figcaption className="text-center text-sm text-gray-500 mt-2">
              {alt}
            </figcaption>
          )}
        </figure>
      );
    },

    // Horizontal rule
    hr: () => <hr className="my-8 border-t border-gray-200" />,
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8">
      <article className="prose prose-lg max-w-none px-6">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={components}
        >
          {markdownContent}
        </ReactMarkdown>
      </article>
    </div>
  );
};

export default MarkdownRenderer;